import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Eye,
  EyeOff,
  Package,
  Smartphone,
  Wrench,
  HelpCircle,
  Filter,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Copy,
  AlertCircle
} from 'lucide-react';
import { trackedAdvertisersApi } from '../services/api';
import type { TrackedAdvertiser, TrackedAdvertiserStats, TrackedAd } from '../types/shared';
import { useToast } from '@/contexts/ToastContext';
import MiniChart from '../components/MiniChart';

const TrackedAdvertisersPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProductType, setSelectedProductType] = useState<string>('all');
  const [expandedAdvertiser, setExpandedAdvertiser] = useState<string | null>(null);
  const [adChanges, setAdChanges] = useState<Record<string, any>>({});

  // Fetch tracked advertisers
  const { data: advertisersData, isLoading: isLoadingAdvertisers } = useQuery(
    ['tracked-advertisers', page, filter],
    () => trackedAdvertisersApi.getTrackedAdvertisers(page, 20, filter === 'active' ? 'true' : filter === 'inactive' ? 'false' : undefined),
    {
      keepPreviousData: true
    }
  );


  // Delete tracking mutation
  const deleteMutation = useMutation(
    (id: string) => trackedAdvertisersApi.deleteTracking(id),
    {
      onSuccess: () => {
        showSuccess('Anunciante eliminado del seguimiento');
        queryClient.invalidateQueries('tracked-advertisers');
      },
      onError: (error: any) => {
        showError(error.response?.data?.message || 'Error al eliminar seguimiento');
      }
    }
  );


  // Fetch ad changes for an advertiser
  const fetchAdChanges = async (advertiserId: string) => {
    try {
      const result = await trackedAdvertisersApi.getAdChanges(advertiserId);
      setAdChanges(prev => ({ ...prev, [advertiserId]: result.data }));
    } catch (error) {
      console.error('Error fetching ad changes:', error);
    }
  };

  // Refresh data when component mounts
  useEffect(() => {
    queryClient.invalidateQueries('tracked-advertisers');
    queryClient.invalidateQueries('tracked-advertisers-stats');
  }, [queryClient]);

  const getProductTypeIcon = (type: string) => {
    switch (type) {
      case 'physical': return <Package className="w-4 h-4 text-blue-500" />;
      case 'digital': return <Smartphone className="w-4 h-4 text-green-500" />;
      case 'service': return <Wrench className="w-4 h-4 text-purple-500" />;
      default: return <HelpCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getProductTypeColor = (type: string) => {
    switch (type) {
      case 'physical': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'digital': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'service': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Función para combinar datos iniciales con estadísticas diarias
  const getCombinedChartData = (advertiser: TrackedAdvertiser) => {
    const chartData = [];

    // Agregar día inicial si existe
    if (advertiser.initialActiveAdsCount > 0) {
      chartData.push({
        date: advertiser.trackingStartDate,
        activeAds: advertiser.initialActiveAdsCount,
        isInitial: true
      });
    }

    // Agregar estadísticas diarias
    if (advertiser.dailyStats && advertiser.dailyStats.length > 0) {
      advertiser.dailyStats.forEach(stat => {
        chartData.push({
          date: stat.date,
          activeAds: stat.activeAds,
          isInitial: false
        });
      });
    }

    // Ordenar por fecha
    return chartData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  // Función para obtener los días más relevantes para las mini cards
  const getRelevantDays = (advertiser: TrackedAdvertiser) => {
    const allData = getCombinedChartData(advertiser);

    if (allData.length <= 4) {
      // Si hay 4 o menos días, mostrar todos
      return allData;
    }

    // Si hay más de 4 días, mostrar: inicial + últimos 3
    const relevantDays = [];

    // Siempre incluir el día inicial si existe
    const initialDay = allData.find(d => d.isInitial);
    if (initialDay) {
      relevantDays.push(initialDay);
    }

    // Agregar los últimos 3 días (sin contar el inicial)
    const nonInitialDays = allData.filter(d => !d.isInitial);
    const lastThreeDays = nonInitialDays.slice(-3);

    relevantDays.push(...lastThreeDays);

    return relevantDays;
  };

  // Filter advertisers based on search term and product type
  const filteredAdvertisers = advertisersData?.data?.filter(advertiser => {
    const matchesSearch = advertiser.pageName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProductType = selectedProductType === 'all' || advertiser.productType === selectedProductType;
    return matchesSearch && matchesProductType;
  }) || [];


  if (isLoadingAdvertisers) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando anunciantes en seguimiento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-stone-100 mb-2">Anunciantes en Seguimiento</h1>
            <p className="text-stone-600">Monitorea el rendimiento diario de tus anunciantes</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-stone-900 rounded-2xl p-4 border border-stone-700 shadow-gum">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar anunciante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-stone-900 border border-stone-600 rounded-xl text-stone-100 placeholder-stone-500 focus:ring-2 focus:ring-secondary-500/30 focus:border-secondary-400"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-4 py-2 bg-stone-900 border border-stone-600 rounded-xl text-stone-100 focus:ring-2 focus:ring-secondary-500/30"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>

            {/* Product Type Filter */}
            <select
              value={selectedProductType}
              onChange={(e) => setSelectedProductType(e.target.value)}
              className="px-4 py-2 bg-stone-900 border border-stone-600 rounded-xl text-stone-100 focus:ring-2 focus:ring-secondary-500/30"
            >
              <option value="all">Todos los tipos</option>
              <option value="physical">Productos Físicos</option>
              <option value="digital">Productos Digitales</option>
              <option value="service">Servicios</option>
              <option value="other">Otros</option>
            </select>
          </div>
        </div>

        {/* Advertisers List */}
        <div className="space-y-4">
          {filteredAdvertisers.length === 0 ? (
            <div className="text-center py-12">
              <Eye className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-medium text-gray-400 mb-2">No hay anunciantes en seguimiento</h3>
              <p className="text-gray-500">Comienza agregando anunciantes desde la página de búsqueda</p>
            </div>
          ) : (
            filteredAdvertisers.map((advertiser) => (
              <div
                key={advertiser._id}
                className="bg-stone-900 rounded-2xl p-6 border border-stone-700 hover:border-stone-600 shadow-gum transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Profile Picture */}
                    {advertiser.pageProfilePictureUrl ? (
                      <img
                        src={advertiser.pageProfilePictureUrl}
                        alt={advertiser.pageName}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    
                    {/* Fallback Avatar */}
                    <div className={`w-12 h-12 bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl flex items-center justify-center text-white font-bold ${advertiser.pageProfilePictureUrl ? 'hidden' : ''}`}>
                      {advertiser.pageName.charAt(0).toUpperCase()}
                    </div>

                    {/* Advertiser Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-stone-100 truncate">
                          {advertiser.pageName}
                        </h3>
                        {advertiser.pageVerification && (
                          <span className="text-green-400 text-sm">✓ Verificado</span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs border ${getProductTypeColor(advertiser.productType)}`}>
                          {getProductTypeIcon(advertiser.productType)}
                          <span className="ml-1 capitalize">{advertiser.productType}</span>
                        </span>
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-stone-500 mb-3 flex-wrap">
                        {advertiser.pageLikeCount && (
                          <span>👥 {formatNumber(advertiser.pageLikeCount)} seguidores</span>
                        )}
                        <span>📊 {advertiser.totalAdsTracked} ads trackeados</span>
                        {/* Removed: Unique/duplicate counts - No longer tracking individual ads to reduce API costs */}
                        <span>📅 Desde {formatDate(advertiser.trackingStartDate)}</span>
                      </div>

                      {advertiser.notes && (
                        <p className="text-sm text-stone-600 mb-3">{advertiser.notes}</p>
                      )}

                      {/* Daily Stats with Mini Chart */}
                      {(advertiser.dailyStats && advertiser.dailyStats.length > 0) || advertiser.initialActiveAdsCount > 0 ? (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-stone-800">Evolución de Anuncios Activos</h4>
                            {(() => {
                              const totalDays = getCombinedChartData(advertiser).length;
                              const trackingDays = Math.floor(
                                (new Date().getTime() - new Date(advertiser.trackingStartDate).getTime()) / (1000 * 60 * 60 * 24)
                              );
                              return (
                                <span className="text-xs text-gray-400">
                                  {totalDays} {totalDays === 1 ? 'día' : 'días'} de datos • {trackingDays} {trackingDays === 1 ? 'día' : 'días'} de tracking
                                </span>
                              );
                            })()}
                          </div>

                          {/* Mini Chart */}
                          <div className="bg-stone-800/50 border border-stone-700 rounded-xl p-4 mb-4">
                            <MiniChart
                              data={getCombinedChartData(advertiser).map(dataPoint => ({
                                date: dataPoint.date,
                                activeAds: dataPoint.activeAds
                              }))}
                              height={80}
                              showTrend={true}
                            />
                          </div>

                          {/* Recent Stats Grid - mostrar días más relevantes */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {getRelevantDays(advertiser).map((dataPoint, index) => (
                              <div key={index} className={`rounded-xl p-3 ${dataPoint.isInitial ? 'bg-blue-950/40 border border-blue-800/50' : 'bg-stone-800/80 border border-stone-700'}`}>
                                {dataPoint.isInitial ? (
                                  <>
                                    <p className="text-xs text-blue-800 font-medium mb-1">📅 Inicio del tracking</p>
                                    <p className="text-xs text-stone-500 mb-1">{formatDate(dataPoint.date)}</p>
                                    <p className="text-sm font-semibold text-stone-100">{dataPoint.activeAds} ads</p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-xs text-stone-500">{formatDate(dataPoint.date)}</p>
                                    <p className="text-sm font-semibold text-stone-100">{dataPoint.activeAds} activos</p>
                                    <p className="text-xs text-stone-500">
                                      {advertiser.dailyStats?.find(stat => stat.date === dataPoint.date)?.newAds || 0} nuevos
                                    </p>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {/* Cambios de Hoy */}
                      {(() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const todayStats = advertiser.dailyStats?.find(
                          stat => new Date(stat.date).getTime() === today.getTime()
                        );
                        
                        if (todayStats && (todayStats.newAdsIds?.length > 0 || todayStats.deactivatedAdsIds?.length > 0 || todayStats.reactivatedAdsIds?.length > 0)) {
                          return (
                            <div className="mt-4 bg-stone-800/50 rounded-xl p-4 border border-stone-700">
                              <h4 className="text-sm font-medium text-stone-800 mb-3 flex items-center">
                                <AlertCircle className="w-4 h-4 mr-2" />
                                Cambios de Hoy
                              </h4>
                              <div className="grid grid-cols-3 gap-3">
                                {todayStats.newAdsIds && todayStats.newAdsIds.length > 0 && (
                                  <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <TrendingUp className="w-4 h-4 text-green-400" />
                                      <span className="text-xs font-medium text-green-300">Nuevos</span>
                                    </div>
                                    <p className="text-lg font-bold text-stone-100">{todayStats.newAdsIds.length}</p>
                                  </div>
                                )}
                                {todayStats.deactivatedAdsIds && todayStats.deactivatedAdsIds.length > 0 && (
                                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <TrendingDown className="w-4 h-4 text-red-400" />
                                      <span className="text-xs font-medium text-red-300">Apagados</span>
                                    </div>
                                    <p className="text-lg font-bold text-stone-100">{todayStats.deactivatedAdsIds.length}</p>
                                  </div>
                                )}
                                {todayStats.reactivatedAdsIds && todayStats.reactivatedAdsIds.length > 0 && (
                                  <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <RefreshCw className="w-4 h-4 text-blue-400" />
                                      <span className="text-xs font-medium text-blue-300">Reactivados</span>
                                    </div>
                                    <p className="text-lg font-bold text-stone-100">{todayStats.reactivatedAdsIds.length}</p>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  if (expandedAdvertiser === advertiser._id) {
                                    setExpandedAdvertiser(null);
                                  } else {
                                    setExpandedAdvertiser(advertiser._id);
                                    fetchAdChanges(advertiser._id);
                                  }
                                }}
                                className="mt-3 text-xs text-blue-400 hover:text-blue-300 underline"
                              >
                                {expandedAdvertiser === advertiser._id ? 'Ocultar detalles' : 'Ver detalles de cambios'}
                              </button>
                              {expandedAdvertiser === advertiser._id && adChanges[advertiser._id] && (
                                <div className="mt-3 space-y-2">
                                  {adChanges[advertiser._id].newAds && adChanges[advertiser._id].newAds.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-green-300 mb-1">Anuncios Nuevos:</p>
                                      <div className="space-y-1">
                                        {adChanges[advertiser._id].newAds.slice(0, 3).map((ad: TrackedAd) => (
                                          <div key={ad._id} className="text-xs text-stone-300 bg-stone-800 border border-stone-600 p-2 rounded-lg">
                                            {ad.snapshot?.body?.text?.substring(0, 100)}...
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {adChanges[advertiser._id].deactivatedAds && adChanges[advertiser._id].deactivatedAds.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-red-300 mb-1">Anuncios Apagados:</p>
                                      <div className="space-y-1">
                                        {adChanges[advertiser._id].deactivatedAds.slice(0, 3).map((ad: TrackedAd) => (
                                          <div key={ad._id} className="text-xs text-stone-300 bg-stone-800 border border-stone-600 p-2 rounded-lg">
                                            {ad.snapshot?.body?.text?.substring(0, 100)}...
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Initial Active Ads Count - Solo mostrar si no hay estadísticas diarias */}
                      {advertiser.initialActiveAdsCount > 0 && (!advertiser.dailyStats || advertiser.dailyStats.length === 0) && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-stone-800 mb-2">
                            Anuncios Activos Iniciales
                          </h4>
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <span className="text-sm font-semibold text-blue-300">
                                {advertiser.initialActiveAdsCount} anuncios activos al momento de agregar
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              Se hace seguimiento diario de este conteo y nuevos anuncios
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <a
                      href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=ALL&view_all_page_id=${advertiser.pageId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center space-x-2"
                      title="Ver anuncios en la biblioteca de anuncios de Facebook"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span className="text-sm font-medium">Ver anuncios</span>
                    </a>

                    <button
                      onClick={() => deleteMutation.mutate(advertiser._id)}
                      disabled={deleteMutation.isLoading}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Eliminar del seguimiento"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {advertisersData?.pagination && advertisersData.pagination.totalPages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 bg-stone-800 border border-stone-600 text-stone-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-700 transition-colors font-medium"
              >
                Anterior
              </button>
              
              <span className="px-4 py-2 text-stone-500">
                Página {page} de {advertisersData.pagination.totalPages}
              </span>
              
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === advertisersData.pagination.totalPages}
                className="px-4 py-2 bg-stone-800 border border-stone-600 text-stone-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-700 transition-colors font-medium"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default TrackedAdvertisersPage;
